/**
 * Download queue, progress and resume — TASK 10.1 Steps 1, 3 and 4.
 *
 * The one place a track's bytes are pulled down, and the one place that decides
 * a download is FINISHED. `lib/offline-store.ts` owns where the bytes go;
 * `sw/service-worker.ts` owns how they come back out; this module owns the
 * lifecycle between the two, and the three telemetry facts it must report.
 *
 * ── SERIAL, NOT PARALLEL ──────────────────────────────────────────────────
 * {@link DOWNLOAD_CONCURRENCY} is 1, and that is a product constraint rather
 * than caution: `GET /api/stream/:trackId` takes a stream lease
 * (`apps/api/src/services/stream-lease.ts`) capped at 2 per user and 10 system
 * wide, and it is EXEMPT from the request-count rate limiter. A parallel
 * downloader would spend both of a user's slots on background work and answer
 * their own next press of PLAY with `STREAM_LIMIT`. One slot for downloading,
 * one left for listening.
 *
 * ── RESUME IS REAL, NOT A RESTART ─────────────────────────────────────────
 * `routes/stream.ts` parses `Range` and answers `206` with Drive's own framing,
 * so an interrupted download resumes from `bytes=<received>-` and the bytes
 * already on the device are never re-fetched. THREE dishonest answers are
 * handled explicitly:
 *
 *   1. a `200` means the server (or Drive) IGNORED the range and is sending the
 *      whole body, so the partial is discarded and the write starts over rather
 *      than concatenating a duplicate prefix;
 *   2. a `416` means the partial is longer than the resource, so it is
 *      discarded too;
 *   3. a `206` whose `Content-Range` does not START where we asked
 *      ({@link rangeStartOf}). This is the one that used to get through: the
 *      status check passes, `totalBytesOf` reads only the size after the last
 *      `/` and sees a correct number, and the body is concatenated onto a
 *      partial it does not continue — producing a duplicated prefix that
 *      commits `done` and is served as authoritative forever.
 *
 * ── AND THE BODY MUST BE WHOLE (Security C5) ──────────────────────────────
 * `commitAudio` is atomic and irreversible, so `transfer` compares `received`
 * against the declared `total` BEFORE committing. A body that ends short
 * without the reader rejecting — a truncating proxy, an under-delivered
 * `Content-Length` — is parked `failed` with its partial intact, not sealed as
 * the canonical track. {@link TRUNCATED_ERROR_CODE} is its own §11 slug.
 *
 * ── QUOTA IS PROJECTED, AND THE PROJECTION IS HONEST (AC 13.6) ────────────
 * `GET /api/albums/:id` does NOT return `size_bytes` (see `albumTrackSchema` —
 * the field exists on the `track` row server-side but is not in the DTO), so
 * the client CANNOT know the exact size before it asks. {@link estimateBytes}
 * therefore projects from duration and container at a deliberately GENEROUS
 * bitrate, so the gate errs toward refusing a download that would have fit
 * rather than starting one that will not. What the gate guarantees is the AC's
 * actual claim: a download PROJECTED to exceed the quota is warned about and
 * never started — no queue entry, no network request.
 *
 * ── BOUNDED EVERYTHING ────────────────────────────────────────────────────
 * {@link MAX_ATTEMPTS} per track, {@link MAX_QUEUE_LENGTH} pending tracks,
 * {@link RESUME_COOLDOWN_MS} between reconnect-driven resumes (a flapping
 * connection must not become a retry storm against the lock-taking stream
 * route), and the offline store's own LRU ceilings behind it.
 *
 * ── TELEMETRY (§11 / W17) ─────────────────────────────────────────────────
 * Exactly two events, on the TERMINAL per-track outcome, through the injected
 * emitter (`lib/telemetry-client.ts`, which owns the allowlist and the PII
 * clamp): `download_completed {track_id, bytes}` and `download_failed
 * {track_id, bytes, error_code}`. `user_id` is deliberately NOT passed — the
 * server attaches it from the session (§11 PII note), and the client's
 * allowlist would drop it anyway.
 *
 * @module
 */

import type { DownloadItem, DownloadTrack, OfflineStore } from './offline-store.js';

/* ── Bounds ────────────────────────────────────────────────────────────── */

/** One in-flight track at a time — see the module header. */
export const DOWNLOAD_CONCURRENCY = 1;
/** Attempts per track before it is parked as `failed` and reported. */
export const MAX_ATTEMPTS = 3;
/** Hard ceiling on pending tracks; an enqueue beyond it is refused. */
export const MAX_QUEUE_LENGTH = 2_000;
/** Minimum gap between two reconnect-driven resume sweeps. */
export const RESUME_COOLDOWN_MS = 30_000;
/** Bytes buffered in memory before a partial is flushed to IndexedDB. */
export const PARTIAL_FLUSH_BYTES = 1 << 20;
/** Fraction of the origin quota this app is willing to occupy. */
export const QUOTA_SAFETY_FRACTION = 0.9;

/**
 * Ceiling on ONE track's transfer, composed with {@link DownloadManager.abortAll}'s
 * signal (Performance W14).
 *
 * The queue is SERIAL by design (see the header), so a body that stalls without
 * erroring blocks not just itself but every download behind it, forever — there
 * is no other worker to make progress. 10 minutes is generous enough for a
 * lossless track on a slow link and short enough that a wedged connection
 * becomes a retriable `failed` row the user can act on rather than a queue that
 * silently stopped.
 */
export const DOWNLOAD_TIMEOUT_MS = 600_000;

/**
 * Ceiling on the opportunistic cover fetch. Much shorter than
 * {@link DOWNLOAD_TIMEOUT_MS}: art is an enhancement, and `whenIdle` awaits it.
 */
export const ART_TIMEOUT_MS = 30_000;

/**
 * `error_code` for a body that ended SHORT of its declared length without the
 * reader ever rejecting (Security C5). Distinct from `network_error` because it
 * is not a transport failure — the transport succeeded and delivered less than
 * it promised, which is the case a truncating proxy produces.
 */
export const TRUNCATED_ERROR_CODE = 'truncated_body';

/**
 * An abort raised by this module rather than by `fetch`.
 *
 * Named `AbortError` so {@link downloadErrorCode} classifies it identically to
 * the platform's own — the §11 slug should say "aborted" whether the abort came
 * from the transport or from {@link DOWNLOAD_TIMEOUT_MS}. `DOMException` is not
 * constructible in every runtime this code is exercised in, hence a class.
 */
export class DomLikeAbortError extends Error {
  constructor() {
    super('The download was aborted.');
    this.name = 'AbortError';
  }
}

/** A body that ended before its declared `total`. Carries no PII. */
export class TruncatedBodyError extends Error {
  readonly received: number;
  readonly expected: number;
  constructor(received: number, expected: number) {
    super('The download ended before the whole body arrived.');
    this.name = 'TruncatedBodyError';
    this.received = received;
    this.expected = expected;
  }
}

/* ── Size projection ───────────────────────────────────────────────────── */

/**
 * Bits per second assumed per container, for the AC 13.6 projection ONLY.
 *
 * Deliberately at or above the realistic ceiling for each format (320 kbps for
 * lossy, ~1 Mbps for FLAC's typical 2:1 on 16/44.1) so the estimate is an
 * OVER-estimate: a gate built on an under-estimate would let a download start
 * and then fail on `QuotaExceededError` halfway, which is exactly the outcome
 * the AC exists to prevent.
 */
export const FORMAT_BITRATES: Readonly<Record<string, number>> = Object.freeze({
  mp3: 320_000,
  m4a: 320_000,
  m4b: 320_000,
  aac: 320_000,
  ogg: 320_000,
  opus: 256_000,
  flac: 1_100_000,
  alac: 1_100_000,
  wav: 1_411_000,
});

/** Fallback for a container the table does not name. */
export const DEFAULT_BITRATE = 1_411_000;

/**
 * Floor for a track whose duration the catalog does not know.
 *
 * `durationS` is `0`/`NaN` for a file the indexer could not probe. Projecting
 * `0` bytes for it meant an unknown-duration track passed the AC 13.6 gate
 * UNCONDITIONALLY — a hundred of them projected a hundred times nothing and
 * sailed through a quota check that exists precisely to stop that. 10 MiB is
 * roughly a 4-minute 320 kbps track: enough that the gate counts it as real,
 * small enough that it does not refuse a download that would obviously fit.
 */
export const UNKNOWN_DURATION_BYTES = 10 << 20;

/** Projected bytes for one track. Pure; see {@link FORMAT_BITRATES}. */
export function estimateBytes(durationS: number, format: string): number {
  // NOT zero — see UNKNOWN_DURATION_BYTES. A gate that cannot measure a track
  // must still charge it something.
  if (!Number.isFinite(durationS) || durationS <= 0) return UNKNOWN_DURATION_BYTES;
  const bitrate = FORMAT_BITRATES[format.toLowerCase()] ?? DEFAULT_BITRATE;
  return Math.ceil((durationS * bitrate) / 8);
}

/**
 * The quota decision, split out as a PURE function so both branches (blocked /
 * allowed) are testable without a browser, and so the "no StorageManager"
 * policy is one readable expression rather than a condition buried in a method.
 *
 * ── WHEN THE RUNTIME WILL NOT SAY (`quotaBytes === null`) THIS FAILS OPEN ──
 * `navigator.storage` is absent or throws in hardened browsers, some private
 * modes, and older WebKit. Failing CLOSED there would make downloads
 * permanently impossible on those runtimes — AC 13.2/13.3 would be
 * unsatisfiable, and the user would be told their storage is full when nothing
 * about their storage is known. Failing OPEN degrades to the pre-quota-API
 * behaviour: the download starts and, if the origin really is full, the
 * `cache.put` raises `QuotaExceededError`, which this module already reports as
 * a per-track `download_failed` with `error_code: 'quota_exceeded'` and which
 * corrupts nothing (the partial is discarded, the record says `failed`). A
 * recoverable, reported failure beats a permanent, unexplained refusal.
 */
export function quotaVerdict(
  projectedBytes: number,
  usedBytes: number,
  quotaBytes: number | null,
  fraction: number = QUOTA_SAFETY_FRACTION,
): { readonly allowed: boolean; readonly availableBytes: number | null } {
  if (quotaBytes === null || quotaBytes <= 0) return { allowed: true, availableBytes: null };
  const available = Math.max(0, Math.floor(quotaBytes * fraction) - usedBytes);
  return { allowed: projectedBytes <= available, availableBytes: available };
}

/* ── Public shapes ─────────────────────────────────────────────────────── */

/** The catalog metadata a track needs to be downloaded AND browsed offline. */
export interface DownloadTrackSpec {
  readonly id: string;
  readonly title: string;
  readonly artist: string;
  readonly album: string;
  readonly albumId: string;
  readonly durationS: number;
  readonly format: string;
}

export interface DownloadRequest {
  /** Stable id for the Downloads row (an album id, playlist id or track id). */
  readonly id: string;
  readonly type: DownloadItem['type'];
  readonly title: string;
  readonly sub: string;
  readonly albumId: string | null;
  readonly tracks: readonly DownloadTrackSpec[];
}

export type EnqueueRefusal = 'quota' | 'duplicate' | 'empty' | 'queue-full' | 'unavailable';

export type EnqueueResult =
  | { readonly started: true; readonly projectedBytes: number }
  | {
      readonly started: false;
      readonly reason: EnqueueRefusal;
      readonly projectedBytes: number;
      readonly availableBytes: number | null;
    };

/** One row of the Downloads view: the catalog metadata plus rolled-up state. */
export interface DownloadSnapshotRow {
  readonly item: DownloadItem;
  readonly status: DownloadTrack['status'];
  /** 0-100, whole numbers. */
  readonly percent: number;
  readonly bytes: number;
  readonly trackCount: number;
  readonly doneCount: number;
}

export interface DownloadSnapshot {
  readonly rows: readonly DownloadSnapshotRow[];
  /**
   * Bytes THIS APP's finished downloads occupy — the sum of `receivedBytes`
   * over `done` tracks. Correct for the per-row size labels, and NOT what the
   * quota gate measures: see {@link DownloadSnapshot.usedBytes}.
   */
  readonly totalBytes: number;
  /**
   * Bytes the ORIGIN occupies, as the browser reports them — the same number
   * {@link quotaVerdict} decides on (Frontend/Product W4).
   *
   * The Downloads view renders this under "Storage used". It used to render
   * `totalBytes` there against the real quota, so the user could read
   * "0.4 GB / 8 GB" directly above a `role="alert"` reading STORAGE NEARLY
   * FULL — two true statements about different quantities, presented as one.
   */
  readonly usedBytes: number;
  readonly quotaBytes: number | null;
}

export interface TelemetryEmitLike {
  emit(event: string, payload: Readonly<Record<string, unknown>>): void;
}

export interface DownloadManagerDeps {
  readonly store: OfflineStore;
  readonly fetchImpl?: typeof fetch;
  readonly telemetry?: TelemetryEmitLike;
  /**
   * Progress/lifecycle fan-out; the view and the live region subscribe.
   *
   * `detail.completed` marks a transition that can have changed WHICH TRACKS
   * HAVE A COMPLETE CACHED BODY — a commit, a delete, a purge. A plain progress
   * flush cannot, and says so, which is what lets the composition root skip a
   * full Cache Storage enumeration per megabyte (Frontend/Performance W10).
   */
  readonly onChange?: (detail?: { readonly completed?: boolean }) => void;
  readonly isOnline?: () => boolean;
  readonly now?: () => number;
  readonly baseUrl?: string;
  readonly warn?: (message: string, detail: Record<string, string | number>) => void;
  /**
   * Bytes buffered before a partial is flushed to IndexedDB. Defaults to
   * {@link PARTIAL_FLUSH_BYTES}.
   *
   * INJECTED so a test can enter the flush loop without allocating megabytes.
   * That is not a convenience: with the constant hard-coded at 1 MiB, every
   * fixture in iteration 1 was ~10 bytes and `drain()`'s flush path — including
   * the `finally { await flush() }` this module calls "the difference between
   * resuming a download and restarting the last megabyte" — was NEVER EXECUTED
   * BY ANY TEST. Making the threshold a parameter is what lets the loop be
   * exercised for real, cheaply, on every run.
   */
  readonly flushBytes?: number;
}

export interface DownloadManager {
  /**
   * The AC 13.6 gate and the only way into the queue. Refuses BEFORE any
   * record is written and BEFORE any request is issued.
   */
  enqueue(request: DownloadRequest): Promise<EnqueueResult>;
  /** Re-queues every failed/incomplete track of one row (the Retry button). */
  retry(itemId: string): Promise<void>;
  /** Deletes one row and frees its bytes. */
  remove(itemId: string): Promise<void>;
  /** Deletes every download and frees every byte (AC 13.3). */
  removeAll(): Promise<void>;
  /** Current rolled-up view state. */
  snapshot(): Promise<DownloadSnapshot>;
  /** Track ids with a COMPLETE cached body — the AvailabilityBadge input. */
  downloadedTrackIds(): Promise<string[]>;
  /** Album ids every one of whose downloaded tracks is complete. */
  downloadedAlbumIds(): Promise<string[]>;
  /** Resumes interrupted work; called on `online` and at boot. Bounded. */
  resume(): Promise<void>;
  /**
   * Resolves when the queue is drained and the opportunistic art fetches have
   * settled.
   *
   * It exists because {@link DownloadManager.enqueue} deliberately does NOT
   * await the transfer — a UI that blocked its Download button until the last
   * byte landed would be unusable. Progress reaches the view through
   * `onChange`, and this is the seam that lets a test (or a shutdown path) wait
   * for the same completion the user waits for, instead of guessing at a number
   * of microtask turns.
   */
  whenIdle(): Promise<void>;
  /** Registers the reconnect listener. Returns its uninstaller. */
  install(target: {
    addEventListener(t: string, l: () => void): void;
    removeEventListener(t: string, l: () => void): void;
  }): () => void;
  /**
   * STOPS EVERYTHING AND STAYS STOPPED. Must be awaited BEFORE
   * `lib/local-purge.ts#purgeLocalState` (Security C1).
   *
   * ── WHY THE PURGE IS NOT ENOUGH ON ITS OWN ──────────────────────────────
   * Sign-out is `await purge(); go('#/signin')`, and `go` is a HASH
   * NAVIGATION, not a reload — the page, and therefore this manager, survives
   * it. Nothing about deleting a database stops a transfer that is already
   * streaming, and both stores RE-CREATE THEMSELVES on the next write:
   * `offline-store.ts#open()` calls `factory.open(...)`, which builds a
   * database that was just deleted, and `withCache` calls `cacheStorage.open(...)`,
   * which builds a cache that was just deleted. So user A's in-flight download
   * would reach its next 1 MiB flush AFTER the purge, re-create the database,
   * re-create the audio cache, commit A's complete audio body and write A's
   * catalog metadata back — and user B, signing in on the same device, would
   * find A's albums on `/downloads` and be served A's bytes cache-first by the
   * service worker, with no network request and no session check.
   *
   * This method closes that by (1) emptying the queue, (2) aborting the
   * in-flight fetch through its `AbortController`, (3) clearing the attempt
   * ledger so nothing re-queues, (4) latching {@link quiesced} so a late
   * continuation cannot re-open anything, and (5) awaiting the pump so the
   * caller knows the last write has landed before it erases.
   *
   * It is TERMINAL for this manager instance. The next signed-in session gets
   * a new one (the app is re-created on the next boot), which is the correct
   * granularity: an instance is bound to a session.
   */
  abortAll(): Promise<void>;
  /** True once {@link abortAll} has run; every entry point is a no-op after. */
  readonly quiesced: boolean;
  /** True while a track is being transferred. */
  readonly busy: boolean;
}

/* ── Error classification ──────────────────────────────────────────────── */

/**
 * §11 `error_code` slug for a failed download. The vocabulary is the client's
 * (dto.ts `PLAY_EVENT_ERROR_CODE_PATTERN`'s header: "the client owns the
 * failure taxonomy"), and every slug produced here matches `/^[a-z][a-z0-9_]*$/`
 * so it is safe for the 90-day log retention.
 */
export function downloadErrorCode(error: unknown): string {
  if (error instanceof HttpStatusError) {
    if (!Number.isInteger(error.status) || error.status < 100 || error.status > 599) {
      return 'http_unknown';
    }
    return `http_${String(error.status)}`;
  }
  if (error instanceof TruncatedBodyError) return TRUNCATED_ERROR_CODE;
  const name = (error as { name?: string } | null)?.name;
  if (name === 'QuotaExceededError') return 'quota_exceeded';
  if (name === 'AbortError') return 'aborted';
  if (name === 'TypeError') return 'network_error';
  return 'download_error';
}

/**
 * The START offset a `206` actually answered with, or `null` when the header is
 * absent or unparseable (Security W2).
 *
 * PURE and exported so the third dishonest answer is table-tested directly.
 * The grammar is RFC 9110 §14.4: `Content-Range: <unit> <first>-<last>/<size>`,
 * where `<size>` may be `*`. Only `bytes` is accepted — a server answering in
 * some other unit has not satisfied the range we sent, and treating its offset
 * as a byte count would be worse than ignoring it.
 */
export function rangeStartOf(response: Response): number | null {
  const raw = response.headers.get('content-range');
  if (raw === null) return null;
  const match = /^\s*bytes\s+(\d+)\s*-\s*(\d+)\s*\/\s*(?:\d+|\*)\s*$/i.exec(raw);
  if (match === null) return null;
  const start = Number(match[1]);
  return Number.isSafeInteger(start) && start >= 0 ? start : null;
}

/** A non-2xx download response. Carries the status the slug is derived from. */
export class HttpStatusError extends Error {
  readonly status: number;
  constructor(status: number) {
    super('The download request failed.');
    this.name = 'HttpStatusError';
    this.status = status;
  }
}

/* ── Manager ───────────────────────────────────────────────────────────── */

export function createDownloadManager(deps: DownloadManagerDeps): DownloadManager {
  const {
    store,
    fetchImpl = (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
    telemetry,
    onChange = () => undefined,
    isOnline = () =>
      (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine !== false,
    now = () => Date.now(),
    baseUrl = '',
    warn = (message, detail) => {
      console.warn(message, detail);
    },
    flushBytes = PARTIAL_FLUSH_BYTES,
  } = deps;

  /** FIFO of track ids still to transfer. Bounded by MAX_QUEUE_LENGTH. */
  let queue: string[] = [];
  /** Attempts spent per track in THIS page lifetime. Bounded by MAX_ATTEMPTS. */
  const attempts = new Map<string, number>();
  /** The in-flight pump, or null when the queue is drained. */
  let pumping: Promise<void> | null = null;
  /** Chained best-effort art fetches, so `whenIdle` can await them too. */
  let artWork: Promise<unknown> = Promise.resolve();
  /**
   * When the last reconnect sweep ran, or `null` for "never".
   *
   * `null` rather than a `0` sentinel: an injected clock (and a device whose
   * clock genuinely reads 0) would make `0` mean both "never resumed" and
   * "resumed at the epoch", which silently disables the cooldown.
   */
  let lastResumeAt: number | null = null;

  /**
   * The in-flight transfer's abort handle, or `null` between transfers.
   * {@link DownloadManager.abortAll} fires it; {@link transfer} composes it
   * with a {@link DOWNLOAD_TIMEOUT_MS} deadline (W14).
   */
  let inFlight: AbortController | null = null;

  /**
   * TERMINAL LATCH (Security C1). Once set, every entry point returns without
   * touching storage, so no continuation that was already scheduled when
   * `abortAll()` ran can re-create the database or the caches the purge is
   * about to delete — or has already deleted.
   */
  let quiesced = false;

  /**
   * Tracks whose records were deleted while their transfer was in flight
   * (Dev W2).
   *
   * `remove()`/`removeAll()` empty the queue and delete the records, but the
   * transfer already running does not stop mid-`drain()`. When it finishes,
   * `commitAudio` finds no partial, returns `false`, and `transfer` throws —
   * so the catch used to write a `failed` track row with no surviving parent
   * AND emit `download_failed{error_code:'download_error'}`. The user pressed
   * DELETE and the product recorded a failure that never happened, permanently
   * skewing the §11 download-failure rate. The catch consults this set before
   * it writes a record or emits anything.
   */
  const cancelled = new Set<string>();

  /**
   * Projected bytes for everything QUEUED OR IN FLIGHT but not yet on disk
   * (Dev W3).
   *
   * `store.usage()` reports what is ALREADY WRITTEN, so a gate built on it
   * alone measures each request against a total that does not include the
   * other requests it has already admitted: N albums that individually fit
   * collectively overrun the quota, and the outcome is exactly the mid-download
   * `QuotaExceededError` the gate exists to prevent. Decremented on every
   * terminal per-track outcome, so it tracks work outstanding rather than work
   * ever requested.
   */
  let pendingProjectedBytes = 0;
  /** Per-track projection, so the decrement matches the increment exactly. */
  const projectedByTrack = new Map<string, number>();

  /**
   * Short-lived cache over `store.usage()` (Frontend/Performance W1).
   *
   * `navigator.storage.estimate()` is the expensive call on this path — WebKit
   * walks the whole origin's storage accounting for it — and `snapshot()` runs
   * it on every repaint, i.e. ~100 times for a 100 MB track, on the same thread
   * draining the stream. The number it returns moves in megabytes over seconds;
   * a 10-second cache is invisible in the UI and removes almost all of the
   * calls. The QUOTA GATE deliberately does NOT use it — see `enqueue`.
   */
  const USAGE_CACHE_MS = 10_000;
  /** @type {{ value: StorageUsageLike, at: number } | null} */
  let usageCache: { value: { usedBytes: number; quotaBytes: number | null }; at: number } | null =
    null;

  async function cachedUsage(): Promise<{ usedBytes: number; quotaBytes: number | null }> {
    const at = now();
    if (usageCache !== null && at - usageCache.at < USAGE_CACHE_MS) return usageCache.value;
    const value = await store.usage();
    usageCache = { value, at };
    return value;
  }

  /** Releases a track's share of {@link pendingProjectedBytes}, once. */
  function releaseProjection(trackId: string): void {
    const projected = projectedByTrack.get(trackId);
    if (projected === undefined) return;
    projectedByTrack.delete(trackId);
    pendingProjectedBytes = Math.max(0, pendingProjectedBytes - projected);
  }

  function emit(event: string, payload: Readonly<Record<string, unknown>>): void {
    try {
      telemetry?.emit(event, payload);
    } catch {
      /* telemetry must never break a download */
    }
  }

  function streamUrl(trackId: string): string {
    return `${baseUrl}/api/stream/${encodeURIComponent(trackId)}`;
  }
  function artUrl(albumId: string): string {
    return `${baseUrl}/api/art/${encodeURIComponent(albumId)}`;
  }

  /* ── the transfer ─────────────────────────────────────────────────────── */

  /**
   * Transfers ONE track to completion. Every exit path leaves the record in a
   * terminal-or-resumable state and, on a terminal one, reports it.
   */
  async function transfer(trackId: string): Promise<void> {
    if (quiesced) return;
    const record = await store.getTrack(trackId);
    if (record === null) return;
    if (record.status === 'done' && (await store.hasAudio(trackId))) return;

    let already = await store.partialLength(trackId);
    await store.putTrack({
      ...record,
      receivedBytes: already,
      status: already > 0 ? 'resuming' : 'downloading',
    });
    onChange();

    /*
     * ONE controller per transfer, composed from two independent deadlines:
     * `abortAll()` (Security C1) and a bounded stall timeout (W14). Composed by
     * hand rather than with `AbortSignal.any`, which is too new to assume and
     * which this repo would then need a polyfill for.
     */
    const controller = new AbortController();
    inFlight = controller;
    const timer = setTimeout(() => {
      controller.abort();
    }, DOWNLOAD_TIMEOUT_MS);

    try {
      const headers: Record<string, string> = {};
      if (already > 0) headers.range = `bytes=${String(already)}-`;
      const response = await fetchImpl(streamUrl(trackId), {
        credentials: 'same-origin',
        signal: controller.signal,
        ...(already > 0 ? { headers } : {}),
      });

      if (response.status === 416) {
        // The partial is longer than the resource (a re-encode shrank it).
        // Discard and start clean rather than commit bytes that do not match.
        await store.clearPartial(trackId);
        throw new HttpStatusError(416);
      }
      if (!response.ok) throw new HttpStatusError(response.status);

      let received = already;
      if (already > 0 && response.status !== 206) {
        // The server ignored the Range and is sending the WHOLE body
        // (routes/stream.ts downgrades to a truthful 200 when Drive does this).
        // Concatenating would duplicate the prefix, so the partial is dropped.
        await store.clearPartial(trackId);
        received = 0;
        already = 0;
      } else if (already > 0 && response.status === 206) {
        /*
         * THE THIRD DISHONEST ANSWER (Security W2). The header used to claim
         * "the two dishonest answers are both handled explicitly"; there are
         * three. A server can answer `206` — satisfying the status check — with
         * a range that does NOT start where we asked: `Range: bytes=500-`
         * answered by `Content-Range: bytes 0-999/1000` is a well-formed 206
         * carrying the WHOLE body. Appending it onto the existing 500-byte
         * partial duplicates the prefix, and the result commits `done` and is
         * served by the service worker as a corrupt track forever.
         *
         * `totalBytesOf` cannot catch this: it reads only the segment after the
         * last `/`, which is the resource SIZE and is correct in this answer.
         */
        const start = rangeStartOf(response);
        if (start !== null && start !== already) {
          await store.clearPartial(trackId);
          received = 0;
          already = 0;
          if (start !== 0) {
            // Not a whole-body answer either — the server is sending a slice we
            // did not ask for and cannot place. Fail rather than guess.
            throw new HttpStatusError(response.status);
          }
        }
      }

      const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
      const total = totalBytesOf(response, received, record.totalBytes);
      received = await drain(response, trackId, received, total, record, controller.signal);

      /*
       * SECURITY C5 — THE COMPLETENESS CHECK, before the commit.
       *
       * `commitAudio` is ATOMIC and irreversible: it seals whatever is in the
       * resume buffer as the canonical `/api/stream/<id>` entry, which the SW
       * then serves cache-first, with no revalidation, forever. A body that
       * ended SHORT without the reader rejecting — a truncating proxy, an
       * under-delivered `Content-Length`, the `body === null` branch — used to
       * sail straight through here, and the `putTrack` below then overwrote
       * `totalBytes` with `received`, DESTROYING THE EVIDENCE that anything was
       * missing.
       *
       * `offline-store.ts` claims a partial "is STRUCTURALLY absent from the
       * only place the service worker ever looks". That was true of the ABORTED
       * case and false of the silently-short one — which is the one that
       * reaches this line. This is what makes the claim true of both.
       */
      if (total > 0 && received !== total) throw new TruncatedBodyError(received, total);

      const committed = await store.commitAudio(trackId, contentType);
      if (!committed) throw new Error('offline-store refused the commit');

      /*
       * Built EXPLICITLY, not spread from `record`. Spreading carried a stale
       * `errorCode` onto a track that failed and then succeeded, contradicting
       * the field's own JSDoc ("present only on `failed`") and leaving a `done`
       * row that reads as if it had gone wrong.
       */
      await store.putTrack({
        trackId: record.trackId,
        itemId: record.itemId,
        title: record.title,
        artist: record.artist,
        album: record.album,
        albumId: record.albumId,
        durationS: record.durationS,
        format: record.format,
        totalBytes: received,
        receivedBytes: received,
        status: 'done',
        updatedAt: now(),
      });
      releaseProjection(trackId);
      // TERMINAL SUCCESS (§11 W17). No user_id — the server attaches it.
      emit('download_completed', { track_id: trackId, bytes: received });
      onChange({ completed: true });
    } catch (error) {
      /*
       * DEV W2 — a delete is not a failure. The user removed this download
       * while it was streaming; there is no record to update, no partial to
       * keep, and emitting `download_failed` here would report a failure that
       * never happened and skew the §11 download-failure rate permanently.
       */
      if (cancelled.has(trackId)) {
        cancelled.delete(trackId);
        releaseProjection(trackId);
        return;
      }
      // Security C1 — the store may already be erased, or about to be. Do not
      // write, do not re-queue, do not report. Just stop.
      if (quiesced) {
        releaseProjection(trackId);
        return;
      }

      const spent = (attempts.get(trackId) ?? 0) + 1;
      attempts.set(trackId, spent);
      const partial = await store.partialLength(trackId);

      if (spent < MAX_ATTEMPTS && isOnline()) {
        // Retriable and still online: leave the partial in place and re-queue.
        await store.putTrack({ ...record, receivedBytes: partial, status: 'queued' });
        queue.push(trackId);
        onChange();
        return;
      }

      const errorCode = downloadErrorCode(error);
      await store.putTrack({ ...record, receivedBytes: partial, status: 'failed', errorCode });
      releaseProjection(trackId);
      // TERMINAL FAILURE (§11 W17). `bytes` reports what actually landed.
      emit('download_failed', { track_id: trackId, bytes: partial, error_code: errorCode });
      warn('download-manager: track failed', { errorCode, attempts: spent });
      onChange({ completed: true });
    } finally {
      clearTimeout(timer);
      if (inFlight === controller) inFlight = null;
    }
  }

  /** Whole-body length, reconciled with a 206's `Content-Range` when present. */
  function totalBytesOf(response: Response, received: number, fallback: number): number {
    const contentRange = response.headers.get('content-range');
    if (contentRange !== null) {
      const slash = contentRange.lastIndexOf('/');
      const size = slash < 0 ? Number.NaN : Number(contentRange.slice(slash + 1));
      if (Number.isFinite(size) && size > 0) return size;
    }
    const length = Number(response.headers.get('content-length'));
    if (Number.isFinite(length) && length > 0) return received + length;
    return fallback;
  }

  /**
   * Streams the body into the resume buffer, flushing every
   * {@link PARTIAL_FLUSH_BYTES} so an interruption loses at most that much and
   * so a long track never holds its whole body in memory.
   */
  async function drain(
    response: Response,
    trackId: string,
    startAt: number,
    total: number,
    record: DownloadTrack,
    signal: AbortSignal,
  ): Promise<number> {
    const body = response.body;
    let received = startAt;

    if (body === null) {
      // No streaming body (a fetch polyfill, or a test double): take it whole.
      // The caller's C5 completeness check covers this branch too — it used to
      // be the one path with no length check of any kind.
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength > 0) received = await store.appendPartial(trackId, buffer);
      return received;
    }

    const reader = body.getReader();
    let pending: Uint8Array[] = [];
    let pendingBytes = 0;

    const flush = async (): Promise<void> => {
      if (pendingBytes === 0) return;
      const merged = new Uint8Array(pendingBytes);
      let offset = 0;
      for (const part of pending) {
        merged.set(part, offset);
        offset += part.byteLength;
      }
      pending = [];
      pendingBytes = 0;
      received = await store.appendPartial(trackId, merged.buffer);
    };

    try {
      for (;;) {
        /*
         * Security C1 — checked EVERY iteration, not just at entry. `fetch`
         * rejects an aborted request, but a body already streaming can keep
         * delivering chunks into a `read()` that was issued before the abort;
         * without this the loop would keep flushing into a store the purge is
         * erasing underneath it.
         */
        if (signal.aborted || quiesced) break;
        const { done, value } = await reader.read();
        if (done) break;
        if (value === undefined) continue;
        pending.push(value);
        pendingBytes += value.byteLength;
        if (pendingBytes >= flushBytes) {
          await flush();
          if (quiesced) break;
          await store.putTrack({
            ...record,
            totalBytes: total,
            receivedBytes: received,
            status: 'downloading',
          });
          onChange();
        }
      }
    } finally {
      /*
       * `finally`, not "after the loop": a connection that dies mid-body makes
       * `read()` REJECT, and bytes buffered since the last flush would simply
       * evaporate. Keeping them is the difference between resuming a download
       * and restarting the last megabyte of it every time the link flaps.
       *
       * ...unless we are QUIESCED, in which case writing anything at all is the
       * cross-account leak `abortAll()` exists to prevent (Security C1). The
       * buffered bytes are dropped on purpose; the whole database is about to
       * be, too.
       */
      if (!quiesced) await flush();
    }
    if (signal.aborted && !quiesced) {
      // An abort that was NOT a sign-out is the W14 stall timeout. Surface it
      // as a real failure so the row is retriable, rather than letting the
      // caller's completeness check mislabel it as a truncated body.
      throw new DomLikeAbortError();
    }
    return received;
  }

  /** Drains the queue serially. Re-entrant-safe: one pump at a time. */
  function pump(): Promise<void> {
    if (pumping !== null) return pumping;
    const run = (async () => {
      while (queue.length > 0) {
        // An offline queue is PARKED, not failed: `resume()` picks it up when
        // the network returns, with the partials intact.
        if (!isOnline()) break;
        const next = queue.shift();
        if (next === undefined) break;
        await transfer(next);
      }
    })().finally(() => {
      pumping = null;
      onChange();
    });
    pumping = run;
    return run;
  }

  /** Best-effort cover capture, so AC 13.2's art survives the network going. */
  async function cacheArt(albumId: string | null): Promise<void> {
    if (albumId === null || albumId === '') return;
    if (quiesced) return;
    if (await store.hasArt(albumId)) return;
    // Bounded (W14) — `whenIdle` awaits this chain, so an art fetch that never
    // settles would hang every caller waiting for the queue to drain.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, ART_TIMEOUT_MS);
    try {
      const response = await fetchImpl(artUrl(albumId), {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      // Re-checked AFTER the await: the purge may have run while this was in
      // flight, and `putArt` would re-create the cache it just deleted.
      if (response.ok && !quiesced) await store.putArt(albumId, response);
    } catch {
      /* art is an enhancement; a missing cover never fails a download */
    } finally {
      clearTimeout(timer);
    }
  }

  /* ── Public surface ───────────────────────────────────────────────────── */

  const manager: DownloadManager = {
    get busy() {
      return pumping !== null;
    },

    get quiesced() {
      return quiesced;
    },

    async abortAll() {
      // Security C1 — see the interface docs for the full attack sequence.
      // ORDER MATTERS: latch FIRST, so anything that wakes up between the
      // lines below sees a stopped manager rather than a half-stopped one.
      quiesced = true;
      queue = [];
      attempts.clear();
      cancelled.clear();
      projectedByTrack.clear();
      pendingProjectedBytes = 0;
      inFlight?.abort();
      inFlight = null;
      // Await the pump so the caller knows the last write has LANDED before it
      // starts erasing — a purge that overlaps an in-flight `put` is the race
      // this method exists to remove, not one it may reintroduce.
      try {
        await pumping;
        await artWork;
      } catch {
        /* a transfer that fails while being aborted is the expected outcome */
      }
      store.close();
    },

    async whenIdle() {
      // A transfer can enqueue a retry, which starts a NEW pump; loop until
      // there is genuinely nothing left rather than awaiting one generation.
      for (let guard = 0; guard < MAX_QUEUE_LENGTH; guard += 1) {
        if (pumping === null && queue.length === 0) break;
        await pumping;
        await artWork;
      }
      await artWork;
    },

    async enqueue(request) {
      const projectedBytes = request.tracks.reduce(
        (sum, track) => sum + estimateBytes(track.durationS, track.format),
        0,
      );

      if (request.tracks.length === 0) {
        return { started: false, reason: 'empty', projectedBytes, availableBytes: null };
      }
      // Security C1 — a quiesced manager belongs to a session that has ended.
      if (quiesced || !store.available) {
        return { started: false, reason: 'unavailable', projectedBytes, availableBytes: null };
      }
      if (queue.length + request.tracks.length > MAX_QUEUE_LENGTH) {
        return { started: false, reason: 'queue-full', projectedBytes, availableBytes: null };
      }
      const existing = await store.listItems();
      if (existing.some((item) => item.id === request.id)) {
        return { started: false, reason: 'duplicate', projectedBytes, availableBytes: null };
      }

      /*
       * AC 13.6 — THE GATE. Everything above is cheap bookkeeping; this is the
       * check the AC names, and it runs BEFORE the first record is written and
       * BEFORE the first request is issued. A refusal leaves the store exactly
       * as it found it: no queue entry, no network traffic.
       */
      /*
       * UNCACHED, deliberately. This is the AC 13.6 gate; it runs once per
       * user-initiated download and must decide on the freshest number the
       * runtime will give it, not on one that may be ten seconds stale.
       */
      const { usedBytes, quotaBytes } = await store.usage();
      usageCache = null;
      /*
       * DEV W3 — CUMULATIVE, not per-request. `usage()` reports bytes already
       * WRITTEN, so measuring each request against it alone lets N albums that
       * individually fit collectively overrun the quota — and the failure mode
       * is the mid-download `QuotaExceededError` this gate exists to prevent.
       * Adding the projection for everything queued-or-in-flight measures the
       * request against the state the device will actually be in when it runs.
       */
      const verdict = quotaVerdict(projectedBytes, usedBytes + pendingProjectedBytes, quotaBytes);
      if (!verdict.allowed) {
        return {
          started: false,
          reason: 'quota',
          projectedBytes,
          availableBytes: verdict.availableBytes,
        };
      }

      const createdAt = now();
      await store.putItem({
        id: request.id,
        type: request.type,
        title: request.title,
        sub: request.sub,
        albumId: request.albumId,
        trackIds: request.tracks.map((track) => track.id),
        createdAt,
      });
      for (const track of request.tracks) {
        const trackProjection = estimateBytes(track.durationS, track.format);
        // Charged NOW, released on the terminal outcome — see
        // `pendingProjectedBytes`.
        projectedByTrack.set(track.id, trackProjection);
        pendingProjectedBytes += trackProjection;
        cancelled.delete(track.id);
        await store.putTrack({
          trackId: track.id,
          itemId: request.id,
          title: track.title,
          artist: track.artist,
          album: track.album,
          albumId: track.albumId,
          durationS: track.durationS,
          format: track.format,
          totalBytes: trackProjection,
          receivedBytes: 0,
          status: 'queued',
          updatedAt: createdAt,
        });
        attempts.delete(track.id);
        queue.push(track.id);
      }
      onChange();

      artWork = artWork.then(() => cacheArt(request.albumId));
      void pump();
      return { started: true, projectedBytes };
    },

    async retry(itemId) {
      if (quiesced) return;
      const tracks = (await store.listTracks()).filter((track) => track.itemId === itemId);
      for (const track of tracks) {
        if (track.status === 'done' && (await store.hasAudio(track.trackId))) continue;
        // A user-initiated retry buys a fresh attempt budget — that is what the
        // button means. It is still bounded: MAX_ATTEMPTS per press.
        attempts.delete(track.trackId);
        cancelled.delete(track.trackId);
        // Re-charged against the cumulative gate (W3): a retried track is
        // outstanding work again, and it was released when it failed.
        if (!projectedByTrack.has(track.trackId)) {
          const projection = estimateBytes(track.durationS, track.format);
          projectedByTrack.set(track.trackId, projection);
          pendingProjectedBytes += projection;
        }
        await store.putTrack({ ...track, status: 'queued' });
        if (!queue.includes(track.trackId)) queue.push(track.trackId);
      }
      onChange();
      await pump();
    },

    async remove(itemId) {
      if (quiesced) return;
      const tracks = (await store.listTracks()).filter((track) => track.itemId === itemId);
      const doomed = new Set(tracks.map((track) => track.trackId));
      queue = queue.filter((id) => !doomed.has(id));
      for (const id of doomed) {
        attempts.delete(id);
        releaseProjection(id);
        /*
         * DEV W2 — mark BEFORE the delete and abort the transfer if it is this
         * track's. The in-flight `transfer()` will fail (its partial is gone),
         * and the marker is what tells its catch that the failure was the
         * user's Delete rather than a download error worth recording.
         */
        cancelled.add(id);
      }
      if (doomed.size > 0) inFlight?.abort();
      usageCache = null;
      await store.deleteItem(itemId);
      onChange({ completed: true });
    },

    async removeAll() {
      if (quiesced) return;
      // Same reasoning as `remove`, over everything that could be in flight.
      for (const id of queue) cancelled.add(id);
      for (const id of projectedByTrack.keys()) cancelled.add(id);
      for (const track of await store.listTracks()) cancelled.add(track.trackId);
      queue = [];
      attempts.clear();
      projectedByTrack.clear();
      pendingProjectedBytes = 0;
      inFlight?.abort();
      usageCache = null;
      await store.deleteAllDownloads();
      onChange({ completed: true });
    },

    async snapshot() {
      const [items, tracks, usage] = await Promise.all([
        store.listItems(),
        store.listTracks(),
        // CACHED (W1) — a repaint per megabyte must not walk the origin's
        // storage accounting every time. The gate below does not use this.
        cachedUsage(),
      ]);
      const byItem = new Map<string, DownloadTrack[]>();
      for (const track of tracks) {
        const bucket = byItem.get(track.itemId);
        if (bucket === undefined) byItem.set(track.itemId, [track]);
        else bucket.push(track);
      }

      let totalBytes = 0;
      const rows = items.map((item): DownloadSnapshotRow => {
        const group = byItem.get(item.id) ?? [];
        const bytes = group.reduce((sum, track) => sum + track.receivedBytes, 0);
        const expected = group.reduce(
          (sum, track) => sum + Math.max(track.totalBytes, track.receivedBytes),
          0,
        );
        totalBytes += group.reduce(
          (sum, track) => sum + (track.status === 'done' ? track.receivedBytes : 0),
          0,
        );
        return {
          item,
          status: rollUp(group),
          percent: expected === 0 ? 0 : Math.min(100, Math.round((bytes / expected) * 100)),
          bytes,
          trackCount: group.length,
          doneCount: group.filter((track) => track.status === 'done').length,
        };
      });

      return { rows, totalBytes, usedBytes: usage.usedBytes, quotaBytes: usage.quotaBytes };
    },

    async downloadedTrackIds() {
      /*
       * Read from CACHE STORAGE, not from the records. A `done` record whose
       * body was evicted by the OS (iOS does exactly this under pressure — the
       * disclosure in the quota warning is about this) would otherwise mark a
       * track playable offline when its bytes are gone. The cache IS the truth
       * about what can be served (AC 13.4).
       */
      return await store.cachedTrackIds();
    },

    async downloadedAlbumIds() {
      const cached = new Set(await store.cachedTrackIds());
      const tracks = await store.listTracks();
      const byAlbum = new Map<string, { total: number; done: number }>();
      for (const track of tracks) {
        const bucket = byAlbum.get(track.albumId) ?? { total: 0, done: 0 };
        bucket.total += 1;
        if (cached.has(track.trackId)) bucket.done += 1;
        byAlbum.set(track.albumId, bucket);
      }
      const out: string[] = [];
      for (const [albumId, counts] of byAlbum) {
        // A PARTIALLY downloaded album is not "downloaded" — offline, its
        // missing tracks must still carry the ⌒ marker (AC 13.4/13.5).
        if (counts.total > 0 && counts.total === counts.done) out.push(albumId);
      }
      return out;
    },

    async resume() {
      if (quiesced) return;
      if (!isOnline()) return;
      if (lastResumeAt !== null && now() - lastResumeAt < RESUME_COOLDOWN_MS) return;
      lastResumeAt = now();

      for (const track of await store.listTracks()) {
        if (track.status === 'done') continue;
        if (queue.includes(track.trackId)) continue;
        // A reconnect restores the attempt budget once per cooldown window —
        // "the network came back" is new information, not a blind retry.
        attempts.delete(track.trackId);
        cancelled.delete(track.trackId);
        if (!projectedByTrack.has(track.trackId)) {
          const projection = estimateBytes(track.durationS, track.format);
          projectedByTrack.set(track.trackId, projection);
          pendingProjectedBytes += projection;
        }
        await store.putTrack({ ...track, status: 'queued' });
        queue.push(track.trackId);
      }
      onChange();
      await pump();
    },

    install(target) {
      const handler = (): void => {
        void manager.resume();
      };
      target.addEventListener('online', handler);
      return () => {
        target.removeEventListener('online', handler);
      };
    },
  };

  return manager;
}

/**
 * Rolls a group of track records up into the ONE status the Downloads row
 * shows. Precedence is the mock's own visual hierarchy (`_downloads`, L1091):
 * a failure is the thing the user must act on, then in-flight work, then
 * queued, and only an all-complete group reads `done`.
 */
export function rollUp(tracks: readonly DownloadTrack[]): DownloadTrack['status'] {
  if (tracks.length === 0) return 'queued';
  if (tracks.some((track) => track.status === 'failed')) return 'failed';
  if (tracks.some((track) => track.status === 'downloading')) return 'downloading';
  if (tracks.some((track) => track.status === 'resuming')) return 'resuming';
  if (tracks.some((track) => track.status === 'queued')) return 'queued';
  return 'done';
}
